/**
 * Cart Validation Module
 * Validates cart quantities based on customer metafields:
 * - customer.custom.available_qty_to_purchase.value
 * - customer.custom.max_purchase_qty.value
 */

const monthlyLimitTemplate = () =>
  window.theme?.translation?.cartValidation?.monthlyLimitExceeded ||
  'This quantity exceeds your maximum monthly purchase limit of {{ max }} for this product.'

const quantityNotAllowedMessage = () =>
  window.theme?.translation?.cartValidation?.quantityNotAllowed || 'This quantity is not allowed.'

const formatMonthlyLimitMessage = (maxQty) =>
  monthlyLimitTemplate().replace(/\{\{\s*max\s*\}\}/g, String(maxQty))

class CartValidation {
  constructor() {
    this.availableQtyList = {}
    this.maxQtyList = {}
    this.cartCache = null
    this.init()
  }

  init() {
    if (!window.customer) return

    try {
      const availableQtyValue = window.customer?.custom?.available_qty_to_purchase?.value
      const maxQtyValue = window.customer?.custom?.max_purchase_qty?.value

      // Handle both string (JSON) and object cases
      if (typeof availableQtyValue === 'string') {
        this.availableQtyList = availableQtyValue ? JSON.parse(availableQtyValue) : {}
      } else if (availableQtyValue && typeof availableQtyValue === 'object') {
        this.availableQtyList = availableQtyValue
      } else {
        this.availableQtyList = {}
      }

      if (typeof maxQtyValue === 'string') {
        this.maxQtyList = maxQtyValue ? JSON.parse(maxQtyValue) : {}
      } else if (maxQtyValue && typeof maxQtyValue === 'object') {
        this.maxQtyList = maxQtyValue
      } else {
        this.maxQtyList = {}
      }
    } catch (error) {
      console.warn('CartValidation: Failed to parse customer metafields', error)
      this.availableQtyList = {}
      this.maxQtyList = {}
    }
  }

  /**
   * Re-read metafields from window.customer (e.g. after augmenting window.customer).
   */
  refresh() {
    this.init()
  }

  /**
   * Resolve metafield map entries when keys do not exactly match `String(product_id)` (Liquid/json
   * quirks). Same idea as bundle-upsell-pwp-product-eligible.liquid.
   * @param {Record<string, unknown>} map
   * @param {string|number} productId
   * @returns {{ found: boolean, value: unknown }}
   */
  #getMapValueForProductId(map, productId) {
    if (!map || typeof map !== 'object' || Array.isArray(map)) {
      return { found: false, value: undefined }
    }

    const pidStr = String(productId).trim()

    if (Object.prototype.hasOwnProperty.call(map, pidStr)) {
      return { found: true, value: map[pidStr] }
    }

    const n = Number(pidStr)
    if (Number.isFinite(n)) {
      if (Object.prototype.hasOwnProperty.call(map, n)) {
        return { found: true, value: map[n] }
      }

      for (const key of Object.keys(map)) {
        const kn = Number(String(key).trim())
        if (Number.isFinite(kn) && kn === n) {
          return { found: true, value: map[key] }
        }
      }
    }

    for (const key of Object.keys(map)) {
      if (String(key).trim() === pidStr) {
        return { found: true, value: map[key] }
      }
    }

    return { found: false, value: undefined }
  }

  blockedMessage() {
    return quantityNotAllowedMessage()
  }

  /**
   * Get current cart to check existing quantities
   */
  async getCart() {
    if (this.cartCache) {
      return this.cartCache
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`)
      this.cartCache = await response.json()
      return this.cartCache
    } catch (error) {
      console.error('CartValidation: Failed to fetch cart', error)
      return { items: [] }
    }
  }

  /**
   * Clear cart cache (call after cart updates)
   */
  clearCartCache() {
    this.cartCache = null
  }

  /**
   * Whether this product has a per-customer purchase cap from metafields.
   * @param {string|number} productId
   * @returns {boolean}
   */
  hasQtyCapForProduct(productId) {
    return this.#getMapValueForProductId(this.availableQtyList, productId).found
  }

  /**
   * @param {string|number} productId
   * @returns {number|null} null when no cap is configured for this product
   */
  getQtyCapForProduct(productId) {
    const { found, value } = this.#getMapValueForProductId(this.availableQtyList, productId)
    if (!found) {
      return null
    }
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }

  /**
   * Purchase cap for limiting GWP lines. Same semantics as {@link validateAddToCart}: if the
   * product id is present in {@link availableQtyList}, the value is the max total units allowed
   * (including 0 = no remaining allowance). Missing key, null, undefined, or empty string → no cap
   * for GWP sync (placeholder rows). NaN → null.
   * @param {string|number} productId
   * @returns {number|null}
   */
  getGwpPurchaseCapOrNull(productId) {
    const { found, value: raw } = this.#getMapValueForProductId(this.availableQtyList, productId)
    if (!found) {
      return null
    }

    if (raw === null || raw === undefined || raw === '') {
      return null
    }

    const cap = Number(raw)
    if (!Number.isFinite(cap)) {
      return null
    }

    return Math.floor(cap)
  }

  /**
   * GWP: cap quantity by promotional/customer limit without blocking the main product.
   * @param {string|number} productId
   * @param {number} desiredQty
   * @param {number} effectiveCartQty — current cart total for this product plus pending qty in the same batch
   * @returns {Promise<{ quantity: number, notice: null | { type: 'capped' | 'already_max', cappedQty?: number } }>}
   */
  async resolveGwpLineQuantity(productId, desiredQty, effectiveCartQty) {
    const pid = String(productId)
    const desired = Math.max(0, Math.floor(Number(desiredQty)) || 0)

    const cap = this.getGwpPurchaseCapOrNull(pid)
    if (cap == null) {
      return { quantity: desired, notice: null }
    }

    const effective = Math.max(0, Math.floor(Number(effectiveCartQty)) || 0)
    const room = Math.max(0, cap - effective)
    const qty = Math.min(desired, room)

    if (qty >= desired) {
      return { quantity: qty, notice: null }
    }

    if (room === 0) {
      return { quantity: 0, notice: { type: 'already_max' } }
    }

    return { quantity: qty, notice: { type: 'capped', cappedQty: qty } }
  }

  /**
   * Get product ID from variant ID
   * @param {string} variantId - Variant ID
   * @returns {Promise<string|null>} Product ID or null if not found
   */
  async getProductIdFromVariant(variantId) {
    try {
      // First, try to get from cart if variant is already in cart (fastest)
      const cart = await this.getCart()
      const cartItem = cart.items?.find((item) => String(item.variant_id) === String(variantId))
      if (cartItem) {
        return String(cartItem.product_id)
      }

      // Fallback: try to fetch variant data from Shopify
      // Use the variants endpoint which returns JSON
      const response = await fetch(`${window.Shopify.routes.root}variants/${variantId}.js`)
      if (response.ok) {
        const variant = await response.json()
        if (variant.product_id) {
          return String(variant.product_id)
        }
      }

      return null
    } catch (error) {
      console.warn('CartValidation: Failed to get product ID from variant', error)
      return null
    }
  }

  /**
   * Get total quantity of a product in cart
   * @param {string} productId - Product ID as string
   * @returns {Promise<number>} Total quantity in cart
   */
  async getCartQuantityForProduct(productId) {
    const cart = await this.getCart()
    let totalQty = 0

    for (const item of cart.items || []) {
      if (String(item.product_id) === String(productId)) {
        totalQty += item.quantity
      }
    }

    return totalQty
  }

  /**
   * Validate quantity for add to cart
   * @param {string} productId - Product ID as string
   * @param {number} requestedQty - Quantity being added
   * @param {number} currentCartQty - Current quantity in cart (optional, will fetch if not provided)
   * @returns {Promise<{allowed: boolean, quantity: number, message: string|null}>}
   */
  async validateAddToCart(productId, requestedQty, currentCartQty = null) {
    const productIdStr = String(productId)

    // Check if product is restricted
    const availEntry = this.#getMapValueForProductId(this.availableQtyList, productIdStr)
    if (!availEntry.found) {
      return {
        allowed: true,
        quantity: requestedQty,
        message: null
      }
    }

    // Get current cart quantity if not provided
    if (currentCartQty === null) {
      currentCartQty = await this.getCartQuantityForProduct(productIdStr)
    }

    const availableQty = availEntry.value
    const newTotalQty = currentCartQty + requestedQty

    // Check if requested quantity is within available limit
    if (newTotalQty <= availableQty) {
      return {
        allowed: true,
        quantity: requestedQty,
        message: null
      }
    }

    // Requested quantity exceeds available - cap to available
    const maxRes = this.#getMapValueForProductId(this.maxQtyList, productIdStr)
    const maxQty = (maxRes.found ? maxRes.value : null) || availableQty
    const allowedQty = Math.max(0, availableQty - currentCartQty)

    return {
      allowed: allowedQty > 0,
      quantity: allowedQty,
      message: formatMonthlyLimitMessage(maxQty)
    }
  }

  /**
   * Validate quantity change in cart
   * @param {string} productId - Product ID as string
   * @param {number} requestedQty - New total quantity requested
   * @param {number} currentItemQty - Current quantity of this specific cart item
   * @param {number} currentCartQty - Current total quantity in cart (optional, will fetch if not provided)
   * @returns {Promise<{allowed: boolean, quantity: number, message: string|null}>}
   */
  async validateQuantityChange(productId, requestedQty, currentItemQty = 0, currentCartQty = null) {
    const productIdStr = String(productId)

    // Check if product is restricted
    const availEntry = this.#getMapValueForProductId(this.availableQtyList, productIdStr)
    if (!availEntry.found) {
      return {
        allowed: true,
        quantity: requestedQty,
        message: null
      }
    }

    // Get current cart quantity if not provided
    if (currentCartQty === null) {
      currentCartQty = await this.getCartQuantityForProduct(productIdStr)
    }

    // Calculate new total: subtract current item qty, add requested qty
    const newTotalQty = currentCartQty - currentItemQty + requestedQty
    const availableQty = availEntry.value

    // Check if requested quantity is within available limit
    if (newTotalQty <= availableQty) {
      return {
        allowed: true,
        quantity: requestedQty,
        message: null
      }
    }

    // Requested quantity exceeds available - cap to available
    const maxRes = this.#getMapValueForProductId(this.maxQtyList, productIdStr)
    const maxQty = (maxRes.found ? maxRes.value : null) || availableQty
    const allowedQty = Math.max(0, availableQty - (currentCartQty - currentItemQty))

    return {
      allowed: allowedQty > 0,
      quantity: allowedQty,
      message: formatMonthlyLimitMessage(maxQty)
    }
  }
}

// Create singleton instance
const cartValidation = new CartValidation()

// Export for use in other modules
window.cartValidation = cartValidation

export default cartValidation
